import React, { useEffect, useState } from 'react';
import Page from '../components/Page';
import { Box, Container, Grid, Typography } from '@mui/material';
import { config } from '../utils/utils';
import { useAppDispatch, useAppSelector } from '../store';
import { findPurchasePlan } from '../store/purchasePlan/api';
import { getSettings } from '../store/settings/api';
import PricingCard from '../components/PricingCard';
import QRBuddySubscriptionDialog from '../components/models/QRBuddySubscriptionDialog';
import useToggle from '../hooks/useToggle';

interface PricingFeature {
  text: string;
}

interface FeatureGroup {
  title: string;
  features: PricingFeature[];
}

export interface PricingPlan {
  code: 'payl' | 'sub' | 'usub';
  title: string;
  subtitle?: string;
  isPopular?: boolean;
  featureGroups: FeatureGroup[];
  details: {
    title: string;
    description: string;
    subtitle: string;
    price: string;
    priceDescription?: string;
    features: string[];
    noteTitle: string;
    noteDescription: string;
  };
}

export const pricingPlans: PricingPlan[] = [
  {
    code: 'payl',
    title: 'Pay-As-You-Go',
    featureGroups: [
      {
        title: 'Platform Features',
        features: [
          { text: 'Platform Access' },
          { text: 'Dealer Analytics Dashboard' },
          { text: 'Salesperson Dashboards' },
          { text: 'Real-Time Rep Alerts' },
        ],
      },
      {
        title: 'AI & Engagement',
        features: [
          { text: 'AI & Engagement' },
          { text: 'Miles™ AI On-the-Lot Agent' },
          { text: 'After-Hours Coverage' },
        ],
      },
      {
        title: 'Terms & Flexibility',
        features: [
          { text: 'Terms & Flexibility' },
          { text: '$40 Per Lead' },
          { text: 'No Contract' },
          { text: 'One-Time Setup Fee' },
          { text: 'Upgrade Anytime' },
        ],
      },
    ],
    details: {
      title: 'Pay-As-You-Go Program Setup',
      description:
        'Everything you need to active your dealership engagement system.',
      subtitle: 'Wallet Credit <span>requirement</span>',
      price: '$1,000',
      priceDescription: 'QR Buddy Wallet',
      features: [
        'QR Buddy operates on a prepaid credit system.',
        'Wallet credits are used to process verified customer engagements.',
        'Wallets can be reloaded in increments of $250.',
      ],
      noteTitle: 'Wallet Charge Scheduled',
      noteDescription: 'in 5 Business Days After Account Activation.',
    },
  },
  {
    code: 'usub',
    title: 'Unlimited Lead Promotion',
    subtitle: 'Unlimited Everything',
    isPopular: true,
    featureGroups: [
      {
        title: 'AI & Engagement',
        features: [
          { text: 'AI & Engagement' },
          { text: 'Miles™ AI On-the-Lot Agent' },
          { text: 'Miles™ AI Web Agent' },
          { text: 'Online Website Lead Capture' },
          { text: '24/7 Customer Coverage' },
        ],
      },
      {
        title: 'Engagement Volume',
        features: [
          { text: 'Unlimited Engagement Volume' },
          { text: 'Flat Monthly Billing' },
          { text: '90-Day Agreement' },
          { text: 'One-Time Setup Fee' },
        ],
      },
    ],
    details: {
      title: 'Unlimited Lead Promotion Setup',
      description:
        "Everything you need to activate your dealership's unlimited engagement system.",
      subtitle: 'Unlimited <span>Lead Subscription</span>',
      price: ' $2,000 $1,500 First 90 Days',
      features: [
        // '90-Day Promotion - $500 Off',
        'Forever Pricing as long as your account is current',
        'Unlimited Leads on Lot & Website',
        'All QR Buddy Features Included',
      ],
      noteTitle: 'Subscription Charge Scheduled',
      noteDescription: 'in 5 Business Days After Account Activation.',
    },
  },
  {
    code: 'sub',
    title: 'Monthly Subscription',
    featureGroups: [
      {
        title: 'Platform Features',
        features: [
          { text: 'Platform Access' },
          { text: 'Dealer & Sales Dashboards' },
          { text: 'Real-Time Rep Alerts' },
        ],
      },
      {
        title: 'Terms & Pricing',
        features: [
          { text: 'Terms & Flexibility' },
          { text: '$10 Per Lead – Anytime' },
          { text: '90-Day Agreement' },
          { text: 'One-Time Setup Fee' },
        ],
      },
      {
        title: 'AI & Engagement',
        features: [
          { text: 'AI & Engagement' },
          { text: 'Miles™ AI On-the-Lot Agent' },
          { text: 'Miles™ AI Web Agent' },
        ],
      },
    ],
    details: {
      title: 'QR Buddy Subscription Setup',
      description:
        'Unlimited platform access with flexible credit-based lead savings',
      subtitle: 'Subscription Service',
      price: '$1,500',
      priceDescription: '/ Monthly',
      features: ['Credit-Based Lead System', '$10 Per Lead - Anytime'],
      noteTitle: 'Subscription & Wallet Charges',
      noteDescription:
        'Scheduled Subscription and wallet charges are scheduled 5 business days after account activation.',
    },
  },
];

const Pricing: React.FC = () => {
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px
  // const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md')); // if screen <= 'ms' for tablet and mobile device

  const dispatch = useAppDispatch();

  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | undefined>(
    undefined
  );
  // const [activeTab, setActiveTab] = useState<string>('sub');
  // const tabs = [
  //   { title: 'Pay Per Lead', code: 'payl' },
  //   { title: 'Subscription', code: 'sub', content: 'Content for Tab 2.' },
  // ];

  // const [isQRPrinterSelected, setIsQRPrinterSelected] = useState(false);
  // const [isPrinterPaperSelected, setIsPrinterPaperSelected] = useState(false);

  // const [preloadWalletCredit, setPreloadWalletCredit] = useState<number>(0);
  // const [subscriptionFee, setSubscriptionFee] = useState<number>(0);

  const {
    toggle: toggleGetStartedModel,
    onOpen: onOpenGetStartedModel,
    onClose: onCloseGetStartedModel,
  } = useToggle();

  const {
    // data: plans,
    limit,
    status: planStatus,
    skip,
    // isLoading: isPlanLoading,
  } = useAppSelector((state) => state.purchasePlan);

  const {
    // dealerSetupFee,
    status: settingsStatus,
    // isLoading: isSettingsLoading,
  } = useAppSelector((state) => state.settings);

  useEffect(() => {
    if (planStatus === 'idle') {
      dispatch(
        findPurchasePlan({
          limit,
          skip,
        })
      );
    }
  }, [dispatch, limit, skip, planStatus]);

  useEffect(() => {
    if (settingsStatus === 'idle') {
      dispatch(
        getSettings({
          id: config.DEALER_SETUP_FEE_ID,
        })
      );
    }
  }, [dispatch, settingsStatus]);

  // useEffect(() => {
  //   plans.forEach((item) => {
  //     if (item.purchaseType === PurchaseType.oneTime) {
  //       setPreloadWalletCredit(
  //         parseFloat((item.creditPrice || '0').toString())
  //       );
  //     } else if (item.purchaseType === PurchaseType.subscription) {
  //       setSubscriptionFee(parseFloat((item.planPrice || '0').toString()));
  //     }
  //   });
  // }, [plans]);

  // const handlePayNow = () => {
  //   // const selectedPlan = plans.find((item) =>
  //   //   activeTab === 'payl'
  //   //     ? item.purchaseType === PurchaseType.oneTime
  //   //     : item.purchaseType === PurchaseType.subscription
  //   // );

  //   const webUrl = `${config.WEB_URL}/plan-terms?plan=${activeTab}&qrp=${isQRPrinterSelected}&pp=${isPrinterPaperSelected}`;
  //   window.open(`${webUrl}`) || window.location.replace(`${webUrl}`);
  // };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toUSD = (n: number) =>
    n.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });

  // const getTabButtonStyle = (isActive: boolean): React.CSSProperties => ({
  //   padding: '10px 24px',
  //   borderRadius: '50px',
  //   border: 'none',
  //   fontSize: '14px',
  //   fontWeight: 600,
  //   cursor: 'pointer',
  //   transition: 'all 0.3s ease',
  //   background: isActive ? AppColors.textGradient : 'transparent',
  //   color: isActive ? '#ffffff' : '#374151',
  //   boxShadow: isActive ? '0 4px 12px rgba(124, 58, 237, 0.3)' : 'none',
  //   outline: 'none',
  // });

  return (
    <>
      <Page title={'Pricing'} sx={{ position: 'relative', overflow: 'hidden' }}>
        <Container
          // width={'100%'}
          maxWidth={false}
          sx={{
            px: '0 !important',
            background: `url('/static/pricing_page/pricing_main_background.png')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              p: { xs: 3, md: 3 },
              background:
                'linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(255, 225, 255, 0.01))',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              position: 'relative',
              overflow: 'clip',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1,
                textAlign: 'center',
                p: { xs: 3, md: 3 },
              }}
            >
              <Typography
                sx={{
                  // pl: 1,
                  fontSize: { xs: 20, sm: 40 },
                  // color: 'white',
                  fontWeight: 500,
                }}
              >
                Start Fast. Scale Smarter.
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 15, md: 25 },
                  //   color: AppColors.darkGrayColor3,
                  mt: 0.5,
                }}
              >
                A flexible, all-in-one customer engagement platform designed to
                capture, convert, and manage every buyer interaction — online
                and on your lot.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              minHeight: '80vh',
              px: 'clamp(24px, 5vw, 48px)',
            }}
          >
            <Grid container display={'flex'} justifyContent={'space-evenly'}>
              {pricingPlans.map((plan, i) => (
                <Grid
                  key={i}
                  size={{ xs: 12, md: i === 1 ? 3.8 : 3.5 }}
                  display='flex'
                  justifyContent={'center'}
                  sx={{
                    mb: { xs: 4, md: 0 },
                    mt: { xs: i === 1 ? 2 : 0, md: 0 },
                  }}
                >
                  <PricingCard
                    plan={plan}
                    onGetStartedClicked={(code: string) => {
                      setSelectedPlan(plan);
                      onOpenGetStartedModel();
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>

        {selectedPlan && (
          <QRBuddySubscriptionDialog
            onClose={onCloseGetStartedModel}
            toggle={toggleGetStartedModel}
            plan={selectedPlan}
            // onGetStartedClicked={handlePayNow}
          />
        )}
      </Page>
    </>
  );
};

export default Pricing;
